// Shared localStorage overlay for festival-2026.json — see that file's header
// and Mugizh-admin/festival.tsx's header comment for why there's no DB table.
// Both the admin Festival page and the topbar FestivalNotification need the
// same "base JSON + overlay" merge, so it lives here once.

export type FestivalCategory = "Holiday" | "Tamil Festival" | "Muhurtham";

export type RawFestivalEntry = {
    date: string;
    day: string;
    tamilName: string;
    englishName: string;
    category: FestivalCategory;
    /** Tamil-calendar day + month, e.g. "தை 14" — mainly used on Muhurtham entries. */
    tamilDate?: string;
};

export type FestivalEntry = RawFestivalEntry & { id: string };

export type FestivalOverlay = {
    /** Edits to base (JSON-shipped) entries, keyed by their derived id. */
    edits: Record<string, RawFestivalEntry>;
    /** Entries added entirely through the admin UI. */
    additions: FestivalEntry[];
};

export const EMPTY_FESTIVAL_OVERLAY: FestivalOverlay = { edits: {}, additions: [] };

const OVERLAY_STORAGE_KEY = "mz_festival_overlay";

// Dispatched after every saveFestivalOverlay() so same-tab listeners (e.g. the
// topbar notification, which stays mounted across navigation) can react —
// the browser's built-in "storage" event only fires in OTHER tabs.
export const FESTIVAL_OVERLAY_CHANGED_EVENT = "mz-festival-overlay-changed";

export function loadFestivalOverlay(): FestivalOverlay {
    try {
        let raw = localStorage.getItem(OVERLAY_STORAGE_KEY);
        if (!raw) return EMPTY_FESTIVAL_OVERLAY;
        let parsed = JSON.parse(raw);
        return {
            edits: parsed?.edits ?? {},
            additions: Array.isArray(parsed?.additions) ? parsed.additions : [],
        };
    } catch {
        return EMPTY_FESTIVAL_OVERLAY;
    }
}

export function saveFestivalOverlay(overlay: FestivalOverlay) {
    try {
        localStorage.setItem(OVERLAY_STORAGE_KEY, JSON.stringify(overlay));
    } catch {
        // best-effort — a full/blocked localStorage shouldn't crash the screen
    }
    window.dispatchEvent(new Event(FESTIVAL_OVERLAY_CHANGED_EVENT));
}

export function mergeFestivalOverlay(base: RawFestivalEntry[], overlay: FestivalOverlay): FestivalEntry[] {
    const baseEntries = base.map((entry, index) => {
        const id = `base-${index}`;
        const edit = overlay.edits[id];
        return { id, ...(edit ?? entry) };
    });
    return [...baseEntries, ...overlay.additions];
}
